<template>
  <div class="wc-row">
    <div class="wc-col wc-col__sm-12 wc-col__md-12">
      <SelfServiceForm
        v-if="!loading"
        v-model="changeNameRequest.startDate"
        :is-loading="loading"
        :isSubmitButtonDisabled="submitDisabled"
        :isSubmitting="isSubmitting"
        :self-service-labels="selfServiceLabels"
        :show-response="showErrorResponse"
        @button:submit="submit"
        @button:abort="abort"
        @button:back="abort"
      >
        <template v-slot:main>
          <NameInput
            @input="updateNameInput"
            class="name-input"
            :enableFirstName="false"
            :enableSalutation="false"
            :nameDate="newNameData"
            @input:status="updateNameInputStatus"
          ></NameInput>
        </template>
      </SelfServiceForm>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import {
  InputStatus,
  type NameData,
  NameInput,
  SelfServiceForm,
  type SelfServiceLabels
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import type { ChangeNameRequest, Partner } from 'partner-api'
import { ResponseError } from 'partner-api'
import { partnerApi } from '@/stores/partnerApiClient'
import { useI18n } from 'vue-i18n'
import { fullNameWithSalutation } from '@/utils/PartnerUtils'

const { t } = useI18n()

const partnerProxyId = ref('')
const showErrorResponse = ref(false)
const loading = ref(true)
const isSubmitting = ref(false)

const selfServiceLabels = ref<SelfServiceLabels>({
  formCardTitle: '',
  formCurrentDataLabel: '',
  formCurrentDataValue: '',
  formDateLabel: '',
  formDateErrorText: '',
  responseText: '',
  responseTitle: '',
  responseTileBackButtonLabel: '',
  errorTitle: '',
  errorText: ''
})

const partner = ref<Partner | null>(null)

const newNameData = ref<NameData>({
  salutation: '',
  title: 'kein Titel',
  firstName: '',
  name: ''
})

const changeNameRequest = reactive<ChangeNameRequest>({
  id: '',
  newTitle: '',
  newName: '',
  startDate: ''
})

const nameInputStatus = ref<InputStatus>(InputStatus.DEFAULT)

const fullNameWithSalutationComputed = computed(() => {
  return partner.value ? fullNameWithSalutation(partner.value) : ''
})

const setErrorResponse = () => {
  selfServiceLabels.value.responseTitle = t(
    'selfServices.response.serviceErrorTitle'
  ).toString()
  selfServiceLabels.value.responseText = t(
    'selfServices.response.serviceErrorText'
  ).toString()
  selfServiceLabels.value.responseTileBackButtonLabel = t(
    'selfServices.button.linkToDetails'
  ).toString()
  showErrorResponse.value = true
}

const submitDisabled = computed(() => {
  return nameInputStatus.value !== InputStatus.VALID
})

onMounted(async () => {
  const partnerProxyIdValue = getPartnerProxyId()
  if (!partnerProxyIdValue) {
    throw new Error('No partner proxy id available!')
  }
  partnerProxyId.value = partnerProxyIdValue
  loading.value = true

  try {
    const response = await partnerApi().getPartner({
      partnerProxyId: partnerProxyId.value
    })
    partner.value = response.data ?? null
    prepareData()
  } catch (err) {
    setErrorResponse()
    if (!(err instanceof ResponseError)) {
      console.error('Unknown error:', err)
    }
  } finally {
    loading.value = false
  }
})

onBeforeUnmount(() => {
  // Cleanup logic if needed
})

const prepareData = () => {
  changeNameRequest.id = partnerProxyId.value

  selfServiceLabels.value = {
    formCurrentDataLabel: t('selfServices.changeName.currentFullName').toString(),
    formCurrentDataValue: '',
    formCurrentInfoText: `${fullNameWithSalutationComputed.value}`,
    formCardTitle: t('selfServices.changeName.cardTitle').toString(),
    formDateLabel: t('selfServices.changeName.whenIsNewNameValid').toString(),
    formDateErrorText: t('selfServices.dateErrorText').toString(),
    responseTitle: t('selfServices.response.thankYouTitle').toString(),
    responseText: t('selfServices.response.thankYouTextName').toString(),
    responseTileBackButtonLabel: t('selfServices.button.linkToDetails').toString(),
    errorTitle: '',
    errorText: ''
  }
}

const submit = async () => {
  isSubmitting.value = true
  changeNameRequest.newTitle = newNameData.value.title
  changeNameRequest.newName = newNameData.value.name

  try {
    await partnerApi().changeName({ changeNameRequest })
    navigate('/danke?name-aendern')
  } catch (err) {
    setErrorResponse()
    if (!(err instanceof ResponseError)) {
      throw new Error('Unknown error:' + err)
    }
  } finally {
    isSubmitting.value = false
  }
}

const abort = () => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
}

const updateNameInputStatus = (inputStatus: InputStatus) => {
  nameInputStatus.value = inputStatus
}

const updateNameInput = (input: NameData) => {
  newNameData.value = input
}
</script>

<style lang="scss" scoped>
@media only screen and (min-width: 427px) {
  .name-input {
    width: 75%;
  }
}
</style>
